/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { useReports } from '../../lib/queries/reports.js';

import { ReportForm } from './report-form.js';
import { ReportRow } from './report-row.js';

interface ReportsViewProps {
  readonly titleKey: string;
  readonly subtitleKey: string;
}

/**
 * Reports screen (Figma Reports) shared by Admin + Finance. A generate panel on
 * top; below, the caller's report jobs with live status (the query polls while
 * any job is still queued/running) and a download once a job has succeeded.
 */
export function ReportsView({ titleKey, subtitleKey }: ReportsViewProps): JSX.Element {
  const { t } = useTranslation();
  const reports = useReports();
  const jobs = reports.data ?? [];

  return (
    <div>
      <PageHeader title={t(titleKey)} subtitle={t(subtitleKey, { count: jobs.length })} />
      <ReportForm />

      <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          {t('reports.recent')}
        </h2>
        <QueryState
          isLoading={reports.isLoading}
          isError={reports.isError}
          isEmpty={jobs.length === 0}
          loadingLabel={t('reports.loading')}
          errorLabel={t('reports.error')}
          emptyLabel={t('reports.empty')}
        >
          <div className="space-y-3">
            {jobs.map((job) => (
              <ReportRow key={job.id} job={job} />
            ))}
          </div>
        </QueryState>
      </div>
    </div>
  );
}
